import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { ChartBarDecreasing, TriangleAlert } from "lucide-react";
import { LogoLoadingSpinner } from "~/components/LoadingSpinner";
import { SelectInput } from "~/components/SelectInput";
import {
    ApiQueryEndpoints,
    QueryResponse,
    API_QUERY_BASE_URLS,
} from "~/modules/filters/types";
import {
    AggregateItem,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { TOKEN_TIMESERIES_GROUP_BY_LABELS } from "~/modules/token_timeseries/consts";
import { NetFlowsChart } from "~/modules/visualization/components/NetFlowsChart/component";
import { getChartHeight } from "~/modules/flows_explorer/pages/list/utils";
import { api } from "~/utils/api";
import { deobfuscate } from "~/utils/obfuscate";
import { formatCurrencyValueCompact } from "~/utils/functions/formatCurrencyValueCompact";
import { ChartConfig } from "~/modules/charts/types";
import {
    DEFAULT_NET_FLOWS_GROUP_BY,
    NET_FLOWS_MAX_ASSETS,
    getNetFlowsApiFilters,
    resolveNetFlowsEndDate,
    toNetFlowsChartConfig,
} from "../ChartForm/flows/netFlowsConfig";
import { VisualizationApiToolsButton } from "~/modules/visualization/components/VisualizationApiToolsButton";
import { DataExportProvider } from "~/modules/downloads/components/DataExportProvider";
import { DownloadButton } from "~/modules/downloads/components/DownloadButton";
import { ExportTableNames } from "~/modules/downloads/types";
import { buildFundFlowsQuery } from "~/modules/fund_flows/lib/buildFundFlowsQuery";

// // // //

const selectFlowsQueryResponse = (data: unknown) => {
    return deobfuscate(data as string) as QueryResponse<AggregateItem>;
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * toCalendarDay
 * Saved net flows dates are the author's local start / end of day as ISO
 * timestamps. Recovers the calendar day they mean, as UTC midnight, by
 * rounding to the nearest UTC midnight (exact for authors within UTC±12), so
 * every viewer queries the same days whatever their own timezone.
 */
function toCalendarDay(iso: string, boundary: "start" | "end"): Date {
    // An end of day is one millisecond before the next local midnight
    const offset = boundary === "end" ? 1 : 0;
    const day = Math.round((new Date(iso).getTime() + offset) / DAY_MS);
    return new Date((day - offset) * DAY_MS);
}

function formatCalendarDay(day: Date): string {
    return day.toLocaleDateString("en-US", {
        timeZone: "UTC",
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

// TODO - pass in props.height

/**
 * NetFlowsChartRenderer
 * Renders a net flows chart for the configured assets and date range.
 */
export function NetFlowsChartProvider(props: {
    title: string;
    description: string;
    config: ChartConfig;
    children: (props: {
        title: string;
        description: string;
        isLoading: boolean;
        isEmpty: boolean;
        chartEl: React.ReactNode;
        emptyEl: React.ReactNode;
        loadingEl: React.ReactNode;
        customControls: React.ReactNode;
    }) => React.ReactNode;
}) {
    const { title, config } = props;

    const netFlowsConfig = useMemo(
        () => toNetFlowsChartConfig(config),
        [config],
    );

    // Open-ended windows (null end_date) resolve to today on every render;
    // the memo key is an ISO string, so the window rolls over after midnight.
    const isOpenEnded = netFlowsConfig.end_date === null;
    const endDate = resolveNetFlowsEndDate(netFlowsConfig);

    const startDay = useMemo(
        () => toCalendarDay(netFlowsConfig.start_date, "start"),
        [netFlowsConfig.start_date],
    );
    const endDay = useMemo(() => toCalendarDay(endDate, "end"), [endDate]);

    // The API compares a UTC `date` column, so send the calendar days as UTC
    // bounds. Local timestamps would shift the window by a day west of UTC
    // (and value the flows at the wrong day's NAV).
    const queryDates = useMemo(
        () => ({
            startDate: startDay.toISOString(),
            endDate: new Date(endDay.getTime() + DAY_MS - 1).toISOString(),
        }),
        [startDay, endDay],
    );

    const dateRangeLabel = `${formatCalendarDay(startDay)} to ${
        isOpenEnded ? "Current Date" : formatCalendarDay(endDay)
    }`;

    const flowsApiFilters = useMemo(
        () => getNetFlowsApiFilters(netFlowsConfig.query),
        [netFlowsConfig.query],
    );

    // Group-bys the viewer can switch between (viewer controls).
    const groupByOptions = netFlowsConfig.groups_by_options ?? [];

    // Default to the configured group-by, falling back to the viewer's first
    // option, then to the net flows default.
    const defaultGroupBy =
        netFlowsConfig.default_group_by ??
        groupByOptions[0] ??
        DEFAULT_NET_FLOWS_GROUP_BY;

    // Set the group-by to the default value.
    const [groupBy, setGroupBy] =
        useState<TokenTimeseriesGroupBy>(defaultGroupBy);

    // Reset the group-by to the default value when the default value changes.
    useEffect(() => {
        setGroupBy(defaultGroupBy);
    }, [defaultGroupBy]);

    const flowsFromDateReq = api.visualization.queryFlowsFromDate.useQuery(
        {
            filters: flowsApiFilters,
            groupBy,
            ...queryDates,
            limit: NET_FLOWS_MAX_ASSETS,
        },
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            select: selectFlowsQueryResponse,
        },
    );

    // Build query for API Tools and Data Export
    const query = buildFundFlowsQuery({
        filters: flowsApiFilters,
        groupBy,
        ...queryDates,
    });

    const isLoading = flowsFromDateReq.isFetching;
    const flowsFromDateItems = (flowsFromDateReq.data?.results ?? []).filter(
        (item) => Math.abs(item.value) >= netFlowsConfig.min_net_flow,
    );

    // ENHANCEMENT - re-enable title
    // const cardTitle = `${title || "Net Flows"} · ${dateRangeLabel}`;
    // const cardTitle = `${title || "Net Flows"}`;
    const subtitle = `Net Flows from ${dateRangeLabel}. Net flows are calculated as the difference between tokens minted and tokens burned.${
        netFlowsConfig.min_net_flow > 0
            ? ` Showing assets with at least $${formatCurrencyValueCompact(netFlowsConfig.min_net_flow)} in absolute net flow.`
            : ""
    }. Last calculated ${format(new Date(), "LLL d, yyyy")}.`;

    // Only surface the group-by control when viewers have more than one option.
    const customControls = (
        <div className="flex items-center gap-3 justify-end self-start">
            {groupByOptions.length > 1 && (
                <>
                    <p className="text-sm text-foreground whitespace-nowrap select-none">
                        Group By
                    </p>
                    <SelectInput
                        name="net-flows--group-by-select"
                        value={groupBy}
                        size="sm"
                        disabled={isLoading}
                        onChange={(updatedValue) => {
                            setGroupBy(updatedValue as TokenTimeseriesGroupBy);
                        }}
                        options={groupByOptions.map((option) => ({
                            value: option,
                            content: {
                                label: TOKEN_TIMESERIES_GROUP_BY_LABELS[option],
                            },
                        }))}
                    />
                </>
            )}

            <VisualizationApiToolsButton
                query={query}
                apiDocsBaseUrl={
                    API_QUERY_BASE_URLS[ApiQueryEndpoints.FundFlowsV4]
                }
                size="xs"
            />

            <DataExportProvider>
                {({ isLoading, exportCsv }) => (
                    <DownloadButton
                        disabled={isLoading}
                        onClick={() => {
                            exportCsv({
                                exportTableName: ExportTableNames.fund_flows_v4,
                                query: query,
                            });
                        }}
                    />
                )}
            </DataExportProvider>
        </div>
    );

    const loadingElement = (
        <div className="flex h-[400px] items-center justify-center">
            <LogoLoadingSpinner size={75} />
        </div>
    );

    const chartEl = (
        <>
            <NetFlowsChart
                height={getChartHeight(flowsFromDateItems.length)}
                items={flowsFromDateItems}
                scale={netFlowsConfig.scale}
                animation={false}
                legendWidth={220}
                showIcon={true}
            />

            <p className="text-xs text-muted-foreground font-light flex items-center gap-2">
                {subtitle}
            </p>
        </>
    );

    const isError = flowsFromDateReq.isError;

    const emptyEl = (
        <div
            className="flex flex-col items-center justify-center px-6 text-center"
            style={{
                height: getChartHeight(10),
            }}
        >
            <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-primary-light">
                {isError ? (
                    <TriangleAlert className="size-6 text-primary" />
                ) : (
                    <ChartBarDecreasing className="size-6 text-primary" />
                )}
            </div>
            <h3 className="mb-2 text-lg font-medium text-foreground">
                {isError ? "Unable to load net flows" : "No net flows to show"}
            </h3>
            <p className="max-w-md text-sm text-secondary-foreground">
                {isError
                    ? "Something went wrong. Refresh the page to try again."
                    : `No mint or burn activity matches this chart from ${dateRangeLabel}.${
                          netFlowsConfig.min_net_flow > 0
                              ? ` Try a wider date range or a lower minimum net flow.`
                              : ` Try a wider date range or different assets.`
                      }`}
            </p>
        </div>
    );

    // A failed query has no items either, so show the empty state instead of
    // a blank chart.
    const isEmpty = !isLoading && flowsFromDateItems.length === 0;

    return (
        <>
            {props.children({
                title,
                description: props.description,
                isLoading,
                isEmpty,
                chartEl,
                emptyEl,
                loadingEl: loadingElement,
                customControls,
            })}
        </>
    );

    //         {isLoading && (
    //         )}
    //         {!isLoading && flowsFromDateItems.length > 0 && (
    //         )}
    //         {!isLoading &&
    //             flowsFromDateItems.length === 0 &&
    //             flowsFromDateReq.isSuccess && (
    //                 <div
    //                     className="flex flex-col items-center justify-center px-6 text-center"
    //                     style={{
    //                         height: getChartHeight(10),
    //                     }}
    //                 >
    //                     <div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-muted text-muted-foreground">
    //                         <BarChart3 className="h-8 w-8" />
    //                     </div>
    //                     <h3 className="text-xl text-foreground">
    //                         No net flows to show
    //                     </h3>
    //                     <p className="mt-2 max-w-md text-muted-foreground">
    //                         No mint or burn activity was found for the selected
    //                         assets from {dateRangeLabel}. Try a different date
    //                         range or set of assets.
    //                     </p>
    //                 </div>
    //             )}
    //     </VisualizationCard>
    // );
}
